import { type BgmName, bgmAssetPath, type SeName, seAssetPath } from "./paths";

/**
 * 音声制御（ui-design.md §7.7 / design.md「アセット（F-006）」）。
 *
 * - **囃子（BGM）は既定でミュートであり、ミュート中は音源を fetch しない**。
 *   `playBgm` は `muted` が真の間、`Audio` インスタンスを一切生成しない
 *   （`new Audio(src)` 自体がブラウザにネットワーク取得を促すため）。
 * - **師範代ボイスも同じミュートに従う**（既定ミュート・ミュート中は fetch しない）。
 * - **打ち音（SE）は既定で入**。BGMのミュート設定とは独立しており、`volume` にのみ従う
 *   （`volume<=0` のときだけ鳴らさない）。
 * - 生成した `Audio` はモジュールスコープでキャッシュし、2回目以降の再生では再取得しない。
 */

let bgmAudio: HTMLAudioElement | null = null;
let bgmName: BgmName | null = null;
const seCache = new Map<SeName, HTMLAudioElement>();
let voiceAudio: HTMLAudioElement | null = null;
let voiceSrc: string | null = null;

/** 打ち音を鳴らす。`key.mp3`（`SeName: "key"`）は技が確定した瞬間のみ呼び出すこと（呼び出し側の責務）。 */
export function playSe(name: SeName, volume: number): void {
  if (volume <= 0) {
    return;
  }
  let audio = seCache.get(name);
  if (!audio) {
    audio = new Audio(seAssetPath(name));
    seCache.set(name, audio);
  }
  audio.volume = Math.min(Math.max(volume, 0), 1);
  audio.currentTime = 0;
  audio.play().catch(() => undefined);
}

/** 囃子を鳴らす。ミュート中は`Audio`を生成しない＝音源を取得しない。 */
export function playBgm(name: BgmName, muted: boolean, volume: number): void {
  if (muted) {
    return;
  }
  if (!bgmAudio || bgmName !== name) {
    bgmAudio?.pause();
    bgmAudio = new Audio(bgmAssetPath(name));
    bgmAudio.loop = true;
    bgmName = name;
  }
  bgmAudio.volume = Math.min(Math.max(volume, 0), 1);
  bgmAudio.play().catch(() => undefined);
}

/** 囃子を止める（ミュートへ切り替わった時・画面を離れる時に呼ぶ）。 */
export function stopBgm(): void {
  bgmAudio?.pause();
}

/**
 * 師範代のセリフ音声を鳴らす。ミュート中は`Audio`を生成しない＝音源を取得しない
 * （BGMと同じ規則。NFR-003 / 自動再生で驚かせない）。
 */
export function playVoice(src: string, muted: boolean, volume: number): void {
  if (muted || volume <= 0) {
    return;
  }
  if (!voiceAudio || voiceSrc !== src) {
    voiceAudio?.pause();
    voiceAudio = new Audio(src);
    voiceSrc = src;
  }
  voiceAudio.volume = Math.min(Math.max(volume, 0), 1);
  voiceAudio.currentTime = 0;
  voiceAudio.play().catch(() => undefined);
}

/** 師範代ボイスを止める（セリフ送り・スキップ・画面離脱時）。 */
export function stopVoice(): void {
  voiceAudio?.pause();
}

/** モジュールスコープのキャッシュをリセットする（テスト専用。本番コードからは呼ばない）。 */
export function resetAudioCacheForTests(): void {
  bgmAudio = null;
  bgmName = null;
  seCache.clear();
  voiceAudio = null;
  voiceSrc = null;
}
